import { useRef, useState } from "react";
import axios from "axios";
import { API, useAuth, useLanguage } from "@/App";
import { Upload, X, Loader2 } from "lucide-react";
import { toast } from "sonner";

/** Multi-image uploader. Returns storage paths via onChange(paths: string[]).
 *
 *  Approval pipeline: when both `listingType` and `listingId` are provided, every
 *  successful upload is ALSO registered in the `media` collection with status
 *  `pending_review`. This keeps new uploads invisible to customers until an admin
 *  approves them in the Admin → Media Review tab.
 *
 *  For brand-new listings where the id isn't known until after submit, the parent
 *  form should leave `listingId` undefined here and instead call `POST /media` for
 *  each path itself after the listing has been created.
 *
 *  Unlimited by default. Pass `max={N}` to cap (used only for the admin attractions tool). */
export const ImageUploader = ({ value = [], onChange, max = null, listingType, listingId, mediaType = "image" }) => {
  const { token } = useAuth();
  const { t } = useLanguage();
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const hasCap = typeof max === "number" && max > 0;
  const willReview = !!(listingType && listingId);

  const handleSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const remaining = hasCap ? max - value.length : files.length;
    if (hasCap && remaining <= 0) {
      toast.error(t(`الحد الأقصى ${max} صور`, `Max ${max} images`));
      return;
    }
    setUploading(true);
    const uploaded = [];
    for (const file of files.slice(0, remaining)) {
      try {
        const fd = new FormData();
        fd.append("file", file);
        const res = await axios.post(`${API}/uploads/image`, fd, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const path = res.data.path;
        uploaded.push(path);
        // Register for admin review when the listing already exists.
        if (willReview) {
          try {
            await axios.post(`${API}/media`, {
              listing_type: listingType,
              listing_id:   listingId,
              url:          path,
              media_type:   mediaType,
            }, { headers: { Authorization: `Bearer ${token}` } });
          } catch (e) {
            // Upload succeeded but review-submission failed — surface a soft warning
            // so the owner knows to retry; the file is still attached to the listing.
            console.warn("media review submission failed", e);
          }
        }
      } catch (err) {
        toast.error(t("فشل رفع الصورة", "Upload failed"));
      }
    }
    setUploading(false);
    if (willReview && uploaded.length) {
      toast.success(t("تم الرفع. الصور قيد المراجعة قبل الظهور للعملاء.", "Uploaded. Images are under review before going live."));
    }
    onChange([...value, ...uploaded]);
    if (inputRef.current) inputRef.current.value = "";
  };

  const removeAt = (idx) => {
    const next = value.filter((_, i) => i !== idx);
    onChange(next);
  };

  return (
    <div className="space-y-3" data-testid="image-uploader">
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
        {value.map((path, idx) => (
          <div key={path} className="relative aspect-square rounded-xl overflow-hidden border border-slate-700 group">
            <img
              src={`${API}/uploads/${path}`}
              alt=""
              className="w-full h-full object-cover"
            />
            <button
              type="button"
              onClick={() => removeAt(idx)}
              data-testid={`remove-image-${idx}`}
              className="absolute top-1 left-1 w-7 h-7 rounded-full bg-black/80 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <X size={14} />
            </button>
          </div>
        ))}
        {(!hasCap || value.length < max) && (
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
            data-testid="upload-image-btn"
            className="aspect-square rounded-xl border-2 border-dashed border-slate-700 hover:border-yellow-500 hover:bg-yellow-500/5 flex flex-col items-center justify-center text-slate-400 hover:text-yellow-500 transition-colors"
          >
            {uploading ? <Loader2 className="animate-spin" size={24} /> : <Upload size={24} />}
            <span className="text-xs mt-1">
              {uploading ? t("جاري الرفع...", "Uploading...") : t("إضافة صور", "Add images")}
            </span>
          </button>
        )}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        multiple
        onChange={handleSelect}
        className="hidden"
        data-testid="image-upload-input"
      />
      <p className="text-xs text-slate-500">
        {hasCap
          ? t(`أقصى عدد ${max} صور · JPG, PNG, WEBP · 10MB كحد أقصى`, `Max ${max} images · JPG, PNG, WEBP · 10MB each`)
          : t(
              "ارفع كل ما تشاء من الصور — تظهر للجمهور بعد مراجعة الإدارة.",
              "Upload as many images as you like — they go live after admin review."
            )}
      </p>
    </div>
  );
};
